import React, { useEffect, useState } from "react";
import { formatCoin, getUserTicketCount } from "../../utils/bondWheelHelpers";
import BondPlayConfirmModal from "../bond-play/BondPlayConfirmModal";

const BondWheelJoinModal = ({
  open,
  game,
  userId,
  userCoinBalance = 0,
  loading = false,
  maxTicketsPerJoin = 100,
  onClose,
  onConfirm,
}) => {
  const [ticketCount, setTicketCount] = useState(1);
  const [step, setStep] = useState("tickets");

  useEffect(() => {
    if (open) {
      setTicketCount(1);
      setStep("tickets");
    }
  }, [open, game?._id]);

  if (!open || !game) return null;

  const entry = Number(game.entryAmount || 0);
  const maxAffordable = entry > 0 ? Math.floor(userCoinBalance / entry) : 0;
  const maxTickets = Math.max(1, Math.min(maxTicketsPerJoin, Math.max(1, maxAffordable || 1)));
  const safeTicketCount = Math.max(1, Math.min(maxTickets, Math.floor(Number(ticketCount) || 1)));
  const totalCost = entry * safeTicketCount;
  const canAfford = userCoinBalance >= totalCost;
  const existingTickets = getUserTicketCount(game, userId);
  const isAdding = existingTickets > 0;
  const balanceAfter = Math.max(0, userCoinBalance - totalCost);

  const setQuantity = (next) => {
    const value = Math.max(1, Math.min(maxTickets, Math.floor(Number(next) || 1)));
    setTicketCount(value);
  };

  const quickQuantities = [1, 2, 5, 10].filter(
    (qty, idx, arr) => qty <= maxTickets && arr.indexOf(qty) === idx
  );

  const handleClose = () => {
    if (!loading) onClose?.();
  };

  const handleCancel = () => {
    if (loading) return;
    if (step === "confirm") {
      setStep("tickets");
      return;
    }
    handleClose();
  };

  const handleConfirm = () => {
    if (loading) return;
    if (step === "tickets") {
      setStep("confirm");
      return;
    }
    if (canAfford) onConfirm?.(safeTicketCount);
  };

  const ticketsStep = (
    <div className="bw-join-tickets">
      <p className="bw-join-meta">
        {formatCoin(entry)} / ticket · Pot <strong>{formatCoin(game.totalPot)} COIN</strong>
      </p>
      <div className="bw-join-qty" aria-label="Ticket quantity">
        <button
          type="button"
          className="bw-join-qty-btn"
          disabled={loading || safeTicketCount <= 1}
          onClick={() => setQuantity(safeTicketCount - 1)}
          aria-label="Fewer tickets"
        >
          −
        </button>
        <div className="bw-join-qty-value">
          <span className="bw-join-qty-label">Tickets</span>
          <strong>{safeTicketCount}</strong>
        </div>
        <button
          type="button"
          className="bw-join-qty-btn"
          disabled={loading || safeTicketCount >= maxTickets}
          onClick={() => setQuantity(safeTicketCount + 1)}
          aria-label="More tickets"
        >
          +
        </button>
      </div>
      {quickQuantities.length > 1 ? (
        <div className="bw-ticket-quick">
          {quickQuantities.map((qty) => (
            <button
              type="button"
              key={qty}
              className={qty === safeTicketCount ? "active" : ""}
              disabled={loading}
              onClick={() => setQuantity(qty)}
            >
              {qty}
            </button>
          ))}
        </div>
      ) : null}
      <p className="bplay-confirm-muted bw-join-hint">
        {isAdding
          ? `You already have ${existingTickets} ticket${existingTickets === 1 ? "" : "s"}.`
          : "Each ticket is one slice on the wheel."}
      </p>
    </div>
  );

  const confirmStep = (
    <>
      <div className="bw-join-total">
        <span className="bw-join-total-label">You pay</span>
        <strong>{formatCoin(totalCost)} COIN</strong>
        <span className="bw-join-total-sub">
          {safeTicketCount} ticket{safeTicketCount === 1 ? "" : "s"} × {formatCoin(entry)}
        </span>
      </div>
      <p>
        Balance <strong>{formatCoin(userCoinBalance)}</strong> →{" "}
        <strong>{canAfford ? formatCoin(balanceAfter) : "Insufficient"}</strong> COIN
      </p>
      {!canAfford ? (
        <p className="bplay-confirm-error">You need {formatCoin(totalCost)} COIN for this purchase.</p>
      ) : null}
    </>
  );

  return (
    <BondPlayConfirmModal
      open={open}
      eyebrow={isAdding ? "Add tickets" : "Join wheel"}
      title={step === "tickets" ? (isAdding ? "How many tickets?" : "Choose tickets") : "Confirm purchase"}
      tone="primary"
      confirmLabel={
        step === "tickets"
          ? `Continue · ${formatCoin(totalCost)}`
          : canAfford
            ? `Buy ${safeTicketCount} · ${formatCoin(totalCost)} COIN`
            : "Insufficient balance"
      }
      cancelLabel={step === "confirm" ? "Back" : "Not now"}
      confirmDisabled={step === "confirm" && !canAfford}
      loading={loading}
      loadingLabel={step === "confirm" ? "Buying…" : "Continue…"}
      onClose={handleClose}
      onCancel={handleCancel}
      onConfirm={handleConfirm}
      description={
        <div className="bw-join-steps">
          <div className="bw-join-stepbar" aria-hidden>
            <span className={step === "tickets" ? "active" : "done"} />
            <span className={step === "confirm" ? "active" : ""} />
          </div>
          {step === "tickets" ? ticketsStep : confirmStep}
        </div>
      }
    />
  );
};

export default BondWheelJoinModal;
